import { useState } from 'react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import api from '../../utils/apiClient'
import useGlobalTheme from '../../hooks/useGlobalTheme'
import AuthNavBar from '../Dashboard/AuthNavBar'
import SidePanel from '../SidePanel/SidePanel'

const YEAR_OPTIONS = [1, 2, 3, 4, 5]

function formatNum(value) {
	if (value == null) return '—'
	return Number(value).toLocaleString()
}

function formatPct(value) {
	if (value == null || !Number.isFinite(value)) return '—'
	return `${(value * 100).toFixed(1)}%`
}

function StatCard({ label, value, sub, isLight, accent }) {
	const accentText = accent === 'red'
		? isLight ? 'text-red-600' : 'text-red-400'
		: accent === 'green'
			? isLight ? 'text-emerald-600' : 'text-emerald-400'
			: isLight ? 'text-sky-700' : 'text-sky-300'
	return (
		<div
			className={`p-4 rounded-lg ring-1 ${
				isLight ? 'bg-white ring-gray-200' : 'bg-white/[0.03] ring-white/10'
			}`}
		>
			<div className={`text-xs uppercase tracking-wide ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
				{label}
			</div>
			<div className={`mt-1 text-2xl font-semibold ${accentText}`}>{value}</div>
			{sub && (
				<div className={`mt-0.5 text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
					{sub}
				</div>
			)}
		</div>
	)
}

function SectionTitle({ children, isLight }) {
	return (
		<h3 className={`text-sm font-semibold mb-2 ${isLight ? 'text-gray-900' : 'text-gray-100'}`}>
			{children}
		</h3>
	)
}

function Research() {
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [shipperInput, setShipperInput] = useState('')
	const [years, setYears] = useState(3)
	const [result, setResult] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const submit = async e => {
		e.preventDefault()
		const q = shipperInput.trim()
		if (q.length < 2) {
			setError('Enter at least 2 characters')
			return
		}
		setLoading(true)
		setError(null)
		try {
			const { data } = await api.get('/api/research/violations/by-shipper', {
				params: { q, years },
			})
			setResult(data)
		} catch (err) {
			console.error('[research] fetch failed:', err)
			setError(err.response?.data?.message || 'Failed to fetch data')
			setResult(null)
		} finally {
			setLoading(false)
		}
	}

	const cellLight = isLight ? 'text-gray-700' : 'text-gray-300'
	const cellHeadCls = isLight
		? 'bg-gray-50 text-gray-500'
		: 'bg-white/[0.03] text-gray-400'
	const rowBorder = isLight ? 'divide-gray-200' : 'divide-white/5'
	const ringCls = isLight ? 'ring-gray-200' : 'ring-white/5'

	return (
		<div className={`min-h-screen ${isLight ? 'bg-white text-gray-700' : 'bg-[#15202b] text-gray-200'}`}>
			<AuthNavBar />
			<SidePanel
				isOpen={isPanelOpen}
				onOpen={() => setIsPanelOpen(true)}
				onClose={() => setIsPanelOpen(false)}
			/>
			<div className="max-w-7xl mx-auto pt-[5vh] pb-8 px-4 sm:px-6 lg:px-8">
				<div className={`p-6 rounded-lg ${
					isLight
						? 'bg-white ring-1 ring-gray-200 shadow-lg'
						: 'bg-[#1a2634] ring-1 ring-white/10 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
				}`}>
					<div className="mb-4">
						<h2 className={`text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>
							Shipper Violation Research
						</h2>
						<p className={`text-xs mt-0.5 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
							Search FMCSA roadside inspections by shipper name. Live data from data.transportation.gov.
						</p>
					</div>

					<form onSubmit={submit} className="flex items-center gap-2 mb-5 flex-wrap">
						<div className="relative flex-1 min-w-[14rem]">
							<MagnifyingGlassIcon
								className={`h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}
							/>
							<input
								value={shipperInput}
								onChange={e => setShipperInput(e.target.value)}
								placeholder="Shipper name (e.g. AMAZON, WALMART, TARGET)"
								className={`w-full text-sm rounded-lg ring-1 pl-8 pr-3 py-1.5 focus:outline-none focus:ring-sky-500/40 ${
									isLight
										? 'bg-white ring-gray-300 text-gray-900 placeholder-gray-400'
										: 'bg-[#15202b] ring-white/10 text-gray-200 placeholder-gray-500'
								}`}
							/>
						</div>
						<select
							value={years}
							onChange={e => setYears(Number(e.target.value))}
							className={`text-sm rounded-lg ring-1 px-3 py-1.5 focus:outline-none focus:ring-sky-500/40 ${
								isLight
									? 'bg-white ring-gray-300 text-gray-900'
									: 'bg-[#15202b] ring-white/10 text-gray-200'
							}`}
						>
							{YEAR_OPTIONS.map(y => (
								<option key={y} value={y}>
									{y === 1 ? '1 year' : `${y} years`}
								</option>
							))}
						</select>
						<button
							type="submit"
							disabled={loading}
							className="px-3 py-1.5 text-sm rounded-lg bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30 hover:bg-sky-500/25 disabled:opacity-50"
						>
							{loading ? 'Searching…' : 'Search'}
						</button>
					</form>

					{error && (
						<div
							className={`mb-4 px-3 py-2 rounded-lg bg-red-500/10 ring-1 ring-red-500/30 text-sm ${
								isLight ? 'text-red-600' : 'text-red-300'
							}`}
						>
							{error}
						</div>
					)}

					{!result && !loading && !error && (
						<div className={`text-sm ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
							Enter a shipper name and search. Results include all carriers ever inspected
							hauling freight that lists this shipper, plus their OOS rates, annual trend,
							and geographic distribution.
						</div>
					)}

					{result && (
						<div className="space-y-6">
							<div>
								<SectionTitle isLight={isLight}>
									Summary — {result.query.shipper} (last {result.query.years} {result.query.years === 1 ? 'year' : 'years'})
								</SectionTitle>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
									<StatCard
										label="Distinct carriers"
										value={formatNum(result.summary.distinctCarriers)}
										sub="ever inspected hauling this shipper"
										isLight={isLight}
									/>
									<StatCard
										label="Inspections"
										value={formatNum(result.summary.totalInspections)}
										sub={`${formatNum(result.summary.totalViolations)} total violations`}
										isLight={isLight}
									/>
									<StatCard
										label="Any-OOS rate"
										value={formatPct(result.summary.anyOosRate)}
										sub={`${formatNum(result.summary.anyOosInspections)} insp. with ≥1 OOS`}
										isLight={isLight}
										accent={result.summary.anyOosRate > 0.15 ? 'red' : 'green'}
									/>
									<StatCard
										label="Driver-OOS rate"
										value={formatPct(result.summary.driverOosRate)}
										sub={`${formatNum(result.summary.driverOosInspections)} driver OOS events`}
										isLight={isLight}
										accent={result.summary.driverOosRate > 0.05 ? 'red' : 'green'}
									/>
								</div>
							</div>

							{result.nameVariants.length > 1 && (
								<div>
									<SectionTitle isLight={isLight}>Matched shipper-name variants</SectionTitle>
									<div className={`flex flex-wrap gap-1.5 text-xs ${cellLight}`}>
										{result.nameVariants.map(v => (
											<span
												key={v.name}
												className={`px-2 py-0.5 rounded-full ring-1 ${
													isLight
														? 'bg-gray-100 ring-gray-200'
														: 'bg-white/[0.03] ring-white/10'
												}`}
											>
												{v.name || '(blank)'} · {formatNum(v.inspections)}
											</span>
										))}
									</div>
								</div>
							)}

							<div>
								<SectionTitle isLight={isLight}>Annual trend</SectionTitle>
								<div className={`overflow-x-auto rounded-lg ring-1 ${ringCls}`}>
									<table className="min-w-full text-sm">
										<thead className={`text-xs uppercase ${cellHeadCls}`}>
											<tr>
												<th className="text-left px-3 py-2 font-medium">Year</th>
												<th className="text-right px-3 py-2 font-medium">Distinct carriers</th>
												<th className="text-right px-3 py-2 font-medium">Inspections</th>
												<th className="text-right px-3 py-2 font-medium">Violations</th>
												<th className="text-right px-3 py-2 font-medium">Any-OOS insp.</th>
												<th className="text-right px-3 py-2 font-medium">OOS rate</th>
											</tr>
										</thead>
										<tbody className={`divide-y ${rowBorder}`}>
											{result.byYear.map(row => (
												<tr key={row.year}>
													<td className={`px-3 py-2 font-medium ${cellLight}`}>{row.year}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.distinctCarriers)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.inspections)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.totalViolations)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.anyOosInspections)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatPct(row.anyOosRate)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							<div>
								<SectionTitle isLight={isLight}>Top haulers by inspection volume</SectionTitle>
								<div className={`overflow-x-auto rounded-lg ring-1 ${ringCls}`}>
									<table className="min-w-full text-sm">
										<thead className={`text-xs uppercase ${cellHeadCls}`}>
											<tr>
												<th className="text-left px-3 py-2 font-medium">Carrier</th>
												<th className="text-left px-3 py-2 font-medium">DOT#</th>
												<th className="text-right px-3 py-2 font-medium">Inspections</th>
												<th className="text-right px-3 py-2 font-medium">OOS events</th>
												<th className="text-right px-3 py-2 font-medium">OOS rate</th>
											</tr>
										</thead>
										<tbody className={`divide-y ${rowBorder}`}>
											{result.topHaulers.map(row => (
												<tr key={`${row.dotNumber}-${row.carrierName}`}>
													<td className={`px-3 py-2 ${cellLight}`}>{row.carrierName}</td>
													<td className={`px-3 py-2 ${cellLight}`}>
														<a
															href={`https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${row.dotNumber}`}
															target="_blank"
															rel="noreferrer"
															className={isLight ? 'text-sky-700 hover:underline' : 'text-sky-300 hover:underline'}
														>
															{row.dotNumber}
														</a>
													</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.inspections)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.oosEvents)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatPct(row.anyOosRate)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							<div>
								<SectionTitle isLight={isLight}>Top inspection states</SectionTitle>
								<div className={`overflow-x-auto rounded-lg ring-1 ${ringCls}`}>
									<table className="min-w-full text-sm">
										<thead className={`text-xs uppercase ${cellHeadCls}`}>
											<tr>
												<th className="text-left px-3 py-2 font-medium">State</th>
												<th className="text-right px-3 py-2 font-medium">Inspections</th>
												<th className="text-right px-3 py-2 font-medium">Any-OOS insp.</th>
												<th className="text-right px-3 py-2 font-medium">OOS rate</th>
											</tr>
										</thead>
										<tbody className={`divide-y ${rowBorder}`}>
											{result.topStates.map(row => (
												<tr key={row.state}>
													<td className={`px-3 py-2 font-medium ${cellLight}`}>{row.state}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.inspections)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatNum(row.anyOosInspections)}</td>
													<td className={`px-3 py-2 text-right ${cellLight}`}>{formatPct(row.anyOosRate)}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default Research
